import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import { Button } from "../../../components/ui";
import { ROUTES } from "../../../routes/paths";
import { errorText } from "../../../lib/http";
import AuthLayout from "../components/AuthLayout";
import BackButton from "../components/BackButton";
import { login, verifyOtp } from "../services/authService";
import AuthUtils from "../utils/session";

const OTP_LENGTH = 6;

/** How long before another code can be asked for. */
const RESEND_SECONDS = 30;

/**
 * Masks the address the code went to: enough of it to recognise as yours,
 * not enough to be worth reading off a shoulder.
 */
const maskEmail = (email: string): string => {
  const [local, domain] = email.split("@");
  if (!domain) return email;
  if (local.length <= 3) return `${local}*****@${domain}`;
  return `${local.slice(0, 3)}*****@${domain}`;
};

/**
 * Step two: the six digits, and the session they earn.
 *
 * The address and password come from the screen before as router state —
 * neither is ever in the URL. Reached without them (a bookmark, a reload of a
 * step that carries no state), there is nothing to verify against and nothing
 * to ask for another code with, so the screen sends the browser back to the
 * start rather than showing six boxes that cannot be answered.
 *
 * The session is created here and nowhere else: this is the first point at
 * which both factors have been accepted.
 */
const VerifyOtpPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const email: string = location.state?.email ?? "";
  // There is no separate "resend" endpoint — the login endpoint is what sends
  // the code, so asking for another one is calling it again. The password
  // has to ride along from the screen before for that to be possible.
  const password: string = location.state?.password ?? "";

  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(RESEND_SECONDS);
  const [error, setError] = useState("");
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    if (!email || !password) {
      navigate(ROUTES.login, { replace: true });
      return;
    }
    inputRefs.current[0]?.focus();
  }, [email, password, navigate]);

  // The countdown that holds the Resend link shut. A second request a moment
  // after the first usually means the mail is slow rather than lost, and two
  // live codes in an inbox is a worse problem than waiting half a minute.
  useEffect(() => {
    if (secondsLeft <= 0) return;

    const timer = window.setTimeout(
      () => setSecondsLeft((current) => current - 1),
      1000
    );
    return () => window.clearTimeout(timer);
  }, [secondsLeft]);

  const isComplete = otp.every((digit) => digit !== "");

  const handleChange = (index: number, value: string) => {
    if (value.length > 1 || !/^\d*$/.test(value)) return;

    const next = [...otp];
    next[index] = value;
    setOtp(next);
    setError("");

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent) => {
    if (event.key === "Backspace") {
      // Backspace in an empty box steps back rather than doing nothing, which
      // is what makes six boxes feel like one field.
      if (!otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else if (otp[index]) {
        const next = [...otp];
        next[index] = "";
        setOtp(next);
      }
    }

    if (event.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }

    if (event.key === "ArrowRight" && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    if (event.key === "Enter" && isComplete) {
      handleVerify();
    }
  };

  /** A code pasted from a mail client arrives as one string — spread it. */
  const handlePaste = (event: React.ClipboardEvent) => {
    event.preventDefault();
    const digits = event.clipboardData
      .getData("text/plain")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);

    if (!digits) return;

    const next = Array.from(
      { length: OTP_LENGTH },
      (_, index) => digits[index] ?? ""
    );
    setOtp(next);
    setError("");

    const firstEmpty = next.findIndex((digit) => digit === "");
    inputRefs.current[firstEmpty === -1 ? OTP_LENGTH - 1 : firstEmpty]?.focus();
  };

  const handleVerify = async () => {
    if (!isComplete || isVerifying) return;

    setIsVerifying(true);
    setError("");

    try {
      const result = await verifyOtp(email, otp.join(""));
      AuthUtils.createSession(
        result.admin,
        result.access_token,
        result.refresh_token
      );
      // `replace`, so Back from the dashboard does not land on a code that has
      // already been spent.
      navigate(ROUTES.dashboard, { replace: true });
    } catch (verifyError) {
      setError(
        errorText(
          verifyError,
          "We could not reach the server. Check your connection and try again."
        )
      );
      setIsVerifying(false);
      inputRefs.current[0]?.focus();
    }
  };

  const handleResend = async () => {
    if (secondsLeft > 0 || isResending || isVerifying) return;

    setIsResending(true);
    setError("");

    try {
      await login(email, password);
      setOtp(Array(OTP_LENGTH).fill(""));
      setSecondsLeft(RESEND_SECONDS);
      inputRefs.current[0]?.focus();
    } catch (resendError) {
      setError(errorText(resendError, "Could not resend the code."));
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthLayout>
      {/* Back to the credentials, not back through history: the browser's own
          back from here is the login screen anyway, but `replace` is what
          keeps a spent code screen out of the stack. */}
      <BackButton to={ROUTES.login} className="mb-5" />

      <h1 className="text-[26px] 3xl:text-[28px] leading-tight font-medium text-ink">
        Verify your account
      </h1>
      {/* The address is set in the page's own ink against the grey of the
          sentence carrying it — it is the one part of this line worth reading,
          since it is what tells somebody whether to go back and fix a typo. */}
      <p className="mt-1.5 text-[14px] text-ink-muted">
        We sent a verification code to email
        <span className="ml-2 font-medium text-ink">{maskEmail(email)}</span>
      </p>

      {/* Six boxes plus the separator must fit a 320px-wide viewport, so the
          boxes start small and only grow from sm up. The dash in the middle is
          what makes six digits read as two groups of three rather than as a
          number nobody can keep their place in. */}
      <div className="mt-8 flex items-center gap-1.5 sm:gap-3">
        {otp.map((digit, index) => (
          <React.Fragment key={index}>
            {index === OTP_LENGTH / 2 && (
              <span className="w-4 text-center text-[#C4CBD1]" aria-hidden>
                -
              </span>
            )}
            <input
              ref={(element) => {
                inputRefs.current[index] = element;
              }}
              type="text"
              inputMode="numeric"
              // Only the first box asks for it: six fields all claiming the
              // one-time code is what makes a browser autofill the same digit
              // into every one of them.
              autoComplete={index === 0 ? "one-time-code" : "off"}
              maxLength={1}
              aria-label={`Digit ${index + 1}`}
              value={digit}
              onChange={(event) => handleChange(index, event.target.value)}
              onKeyDown={(event) => handleKeyDown(index, event)}
              onPaste={handlePaste}
              // Selecting on focus means typing over a filled box replaces the
              // digit instead of being refused by the length check.
              onFocus={(event) => event.target.select()}
              disabled={isVerifying}
              className={`w-full min-w-0 aspect-square sm:w-[52px] sm:h-[52px] sm:aspect-auto text-center text-base sm:text-lg font-medium bg-white border rounded-lg focus:outline-none focus:border-[#00C2CB] transition-colors ${
                error ? "border-[#D14343]" : "border-[#DCE0E4]"
              } ${isVerifying ? "opacity-50 cursor-not-allowed" : ""}`}
            />
          </React.Fragment>
        ))}
      </div>

      {error && (
        <p role="alert" className="mt-2 text-xs text-[#D14343]">
          {error}
        </p>
      )}

      <Button
        variant="primary"
        size="lg"
        fullWidth
        onClick={handleVerify}
        disabled={!isComplete || isVerifying}
        className="mt-5"
      >
        {isVerifying ? (
          <>
            <RefreshCw className="w-4 h-4 animate-spin" />
            Verifying...
          </>
        ) : (
          "Verify"
        )}
      </Button>

      <p className="mt-8 text-center text-[14px] text-[#3A4652]">
        Didn&rsquo;t receive OTP?{" "}
        {secondsLeft > 0 ? (
          <>
            Resend{" "}
            <span className="font-semibold text-brand">{secondsLeft}s</span>
          </>
        ) : (
          <button
            type="button"
            onClick={handleResend}
            disabled={isResending || isVerifying}
            className="font-semibold text-brand hover:underline disabled:opacity-50"
          >
            {isResending ? "Sending..." : "Resend"}
          </button>
        )}
      </p>
    </AuthLayout>
  );
};

export default VerifyOtpPage;
